import { ConsentAwareContract } from '@/services/contracts/consent-aware.contract'
import MouseflowService from '@/services/mouseflow.service'
import AbTastyService from '@/services/abtasty.service'
import ProductFruitsService from '@/services/product-fruits.service'
import CalendlyService from '@/services/calendly.service'
import GoogleTagManagerService from '@/services/google-tag-manager.service'
import AmplitudeService from '@/services/amplitude/amplitude.service'
import { DomStorage } from '@/enums'
import { UcUiCmpEvent } from './interfaces/uc-ui-cmp-event.interface'
import { ConsentInfo } from './interfaces/consent-info.interface'
import { ConsentData } from './interfaces/consent-data.interface'

class UsercentricsService implements ConsentAwareContract {
  activate(): void | Promise<void> {
    this.registerEventListeners()
    this.loadScript()
  }

  deactivate(): void | Promise<void> {
    return undefined
  }

  private getRegisteredServices() {
    return [
      { key: 'Google Tag Manager', service: GoogleTagManagerService },
      { key: 'Mouseflow', service: MouseflowService },
      { key: 'Product Fruits', service: ProductFruitsService },
      { key: 'Calendly', service: CalendlyService },
      { key: 'AB Tasty', service: AbTastyService },
      { key: 'Amplitude', service: AmplitudeService },
    ]
  }

  private registerEventListeners() {
    this.registerUcUiCmpEventListener()
    this.registerUcUiInitializedEventListener()
  }

  private registerUcUiInitializedEventListener() {
    window.addEventListener('UC_UI_INITIALIZED', () => {
      this.initServices()
    })
  }

  private registerUcUiCmpEventListener() {
    /**
     * For a list of all events and their parameters,
     * see: https://usercentrics.com/docs/web/features/v2/events_v2/events_v2/#uc_ui_cmp_event
     */
    window.addEventListener('UC_UI_CMP_EVENT', (event: UcUiCmpEvent) => {
      if (['ACCEPT_ALL', 'DENY_ALL', 'SAVE'].includes(event.detail.type)) {
        this.initServices()
      }
    })
  }

  private loadScript() {
    const usercentricsId =
      window.location.href.includes('enterprise') ||
      window.location.href.includes('/b2b') ||
      window.location.href.includes('unternehmensportal')
        ? 'yzBr-PNKx'
        : '4oM0nBM3V'

    if (usercentricsId && !window.location.search.includes('testmode')) {
      setTimeout(() => {
        const tag = document.createElement('script')

        tag.src = 'https://app.usercentrics.eu/browser-ui/latest/loader.js'
        tag.id = 'usercentrics-cmp'
        tag.setAttribute('data-settings-id', usercentricsId)
        tag.setAttribute('defer', 'true')
        tag.setAttribute(
          'lang',
          document.querySelector('html')?.getAttribute('lang') || 'en',
        )
        document.querySelector('body')?.appendChild(tag)

        const reStyle = () => {
          const usercentricsRoot = document.querySelector('#usercentrics-root')
          if (
            !usercentricsRoot?.shadowRoot?.querySelector(
              '[data-testid="uc-anchors"]',
            )
          ) {
            setTimeout(reStyle, 100)
            return
          }

          const ucRoot = usercentricsRoot.shadowRoot
          const anchorsElement = ucRoot.querySelector(
            '[data-testid="uc-anchors"]',
          )
          const footerElement = ucRoot.querySelector(
            '[data-testid="uc-footer"]',
          )

          if (anchorsElement?.parentNode && footerElement) {
            const parentNode = anchorsElement.parentNode as HTMLElement
            parentNode.style.marginTop = '-5px'
            parentNode.style.marginLeft = '8px'
            parentNode.style.marginBottom = '20px'

            const anchorsParentHtml = parentNode.outerHTML
            footerElement.insertAdjacentHTML('beforeend', anchorsParentHtml)

            parentNode.style.display = 'none'
          }

          const ucStyle = document.createElement('link')
          ucStyle.href = '/styles/usercentrics.css'
          ucStyle.rel = 'stylesheet'
          ucStyle.type = 'text/css'
          ucRoot.appendChild(ucStyle)
        }

        reStyle()
      }, 2000)
    }
  }

  public suppressUsercentricsConsentModal() {
    window.UC_UI_SUPPRESS_CMP_DISPLAY = true
  }

  private initServices(): void {
    const registeredServices = this.getRegisteredServices()

    const consents = window.UC_UI.getServicesBaseInfo().map(
      (info: ConsentInfo): ConsentData => {
        return {
          name: info.name,
          consentGiven: info.consent.status,
        }
      },
    )

    const consentedServices = consents.filter(
      (consent: ConsentData) => consent.consentGiven,
    )
    registeredServices.forEach((registeredService) => {
      const consent = consentedServices.find(
        (consent: ConsentData) => consent.name === registeredService.key,
      )

      if (consent) {
        registeredService.service.activate()
        console.log(
          `[Usercentrics] Activated service: ${registeredService.key}`,
        )
      } else {
        registeredService.service.deactivate()
        console.log(
          `[Usercentrics] Deactivated service: ${registeredService.key}`,
        )
      }
    })
  }

  public hasUserInteractedWithUsercentrics(): boolean {
    return localStorage.getItem(DomStorage.USERCENTRICS_INTERACTION) === 'true'
  }
}

export default new UsercentricsService()
